import i18n from '../i18n';
import { getLanguage } from '../i18n/registry';

const formattingLocale = () => getLanguage(i18n.language, 'ui').formatLocale;
let formattingTimezone;

export const setFormattingTimezone = timezone => {
  try {
    // Only accept actual Intl timezones; language never selects a timezone.
    new Intl.DateTimeFormat(undefined, { timeZone: timezone }).format();
    formattingTimezone = timezone;
  } catch (_) { /* Keep the last valid preference. */ }
};

// Utility functions for timezone conversion

/**
 * Get the user's timezone information
 * @returns {Object} User's timezone info including name and offset
 */
export const getUserTimezone = () => {
  try {
    // Get the user's timezone name (e.g., "America/New_York")
    const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Get the timezone offset in minutes (negative for timezones ahead of UTC)
    const timezoneOffset = new Date().getTimezoneOffset();

    return {
      name: timezoneName,
      offsetMinutes: timezoneOffset
    };
  } catch (error) {
    console.error('Error getting user timezone:', error);
    // Fallback to UTC if timezone detection fails
    return {
      name: 'UTC',
      offsetMinutes: 0
    };
  }
};

/**
 * Convert server timestamp to user's local timezone
 * @param {string} serverTimestamp - Timestamp from server (SQLite CURRENT_TIMESTAMP format)
 * @returns {Date} Date object adjusted for user's timezone
 */
export const convertToUserTimezone = (serverTimestamp) => {
  if (!serverTimestamp) {
    return new Date();
  }

  try {
    // Parse the server timestamp - SQLite CURRENT_TIMESTAMP is in UTC
    let serverDate = new Date(serverTimestamp);

    // If the date is invalid, return current time
    if (isNaN(serverDate.getTime())) {
      console.warn('Invalid timestamp:', serverTimestamp);
      return new Date();
    }

    // Check if the timestamp appears to be missing timezone info
    // SQLite CURRENT_TIMESTAMP format is "YYYY-MM-DD HH:MM:SS" (no timezone)
    // If so, we need to treat it as UTC and let the browser convert to local time
    if (typeof serverTimestamp === 'string' && !serverTimestamp.includes('T') && !serverTimestamp.includes('Z')) {
      // Convert "YYYY-MM-DD HH:MM:SS" to "YYYY-MM-DDTHH:MM:SS.000Z" (explicit UTC)
      const isoString = serverTimestamp.replace(' ', 'T') + '.000Z';
      serverDate = new Date(isoString);
    }

    return serverDate;
  } catch (error) {
    console.error('Error converting timestamp:', error);
    return new Date();
  }
};

/**
 * Format a timestamp for display in the user's timezone
 * @param {string} serverTimestamp - Timestamp from server
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string in user's timezone
 */
export const formatTimestampForUser = (serverTimestamp, options = {}) => {
  const date = convertToUserTimezone(serverTimestamp);

  const defaultOptions = {
    timeZone: formattingTimezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  };

  try {
    // Use Intl.DateTimeFormat for more reliable formatting
    return new Intl.DateTimeFormat(formattingLocale(), defaultOptions).format(date);
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    // Fallback to basic toLocaleString
    return date.toLocaleString(formattingLocale());
  }
};

/**
 * Format timestamp for display (date only)
 * @param {string} serverTimestamp - Timestamp from server
 * @returns {string} Formatted date string in user's timezone
 */
export const formatDateForUser = (serverTimestamp) => {
  return formatTimestampForUser(serverTimestamp, {
    hour: undefined,
    minute: undefined,
    timeZoneName: undefined
  });
};

/**
 * Format timestamp for display (time only)
 * @param {string} serverTimestamp - Timestamp from server
 * @returns {string} Formatted time string in user's timezone
 */
export const formatTimeForUser = (serverTimestamp) => {
  return formatTimestampForUser(serverTimestamp, {
    year: undefined,
    month: undefined,
    day: undefined,
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Get relative time string (e.g., "2 hours ago")
 * @param {string} serverTimestamp - Timestamp from server
 * @returns {string} Relative time string
 */
export const getRelativeTime = (serverTimestamp) => {
  const date = convertToUserTimezone(serverTimestamp);
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const relative = new Intl.RelativeTimeFormat(formattingLocale(), { numeric: 'auto' });
  if (Math.abs(seconds) < 60) return relative.format(0, 'second');
  if (Math.abs(seconds) < 3600) return relative.format(Math.trunc(seconds / 60), 'minute');
  if (Math.abs(seconds) < 86400) return relative.format(Math.trunc(seconds / 3600), 'hour');
  if (Math.abs(seconds) < 604800) return relative.format(Math.trunc(seconds / 86400), 'day');
  return formatDateForUser(serverTimestamp);
};
